// Dashboard: processor health (always), then — once a processor is chosen — counts and cardholders by program.
import { Icon } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi, type CardholderSummary, type ProbeResult, type ProgramSummary } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { useStaff } from '../components/RequireStaff';
import { PageHeading } from '../components/ui';
import { effectiveProgramId, type Scope } from '../scope';

const TILE_CLASSES = ['qc-stat-tile--accent', 'qc-stat-tile--success', 'qc-stat-tile--purple', 'qc-stat-tile--red'];
const TILE_ICONS = [
  <path
    key="0"
    d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6Zm2 3h16M7 15h4"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    fill="none"
  />,
  <g key="1">
    <circle cx="9" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path
      d="M3.5 19c.8-3.2 3-5 5.5-5s4.7 1.8 5.5 5M16 10l2 2 3.5-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </g>,
  <path
    key="2"
    d="M4 8h13l-3-3M20 16H7l3 3"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="none"
  />,
  <path
    key="3"
    d="m12 3 8 4.5-8 4.5-8-4.5L12 3Z M4 12.5 12 17l8-4.5M4 16.5 12 21l8-4.5"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="none"
  />,
];

export interface ProcessorStatus {
  name: string;
  reachable: boolean;
  message: string;
  elapsedMs: number;
}

/** DashboardModel.ToStatus */
export const processorStatus = (name: string, probe: ProbeResult | null | undefined): ProcessorStatus => ({
  name,
  reachable: probe?.reachable ?? false,
  message: probe?.message ?? "Probe failed -- the admin console couldn't reach the Api.",
  elapsedMs: probe?.elapsedMs ?? 0,
});

/** The four tiles and the program breakdown (ZumRails programs hidden from the count). */
export function dashboardFigures(
  scope: Scope,
  cardholders: { items: CardholderSummary[]; totalCount: number },
  transactionCount: number,
  programs: ProgramSummary[],
) {
  const visiblePrograms = programs.filter(
    (p) => p.provider !== 'ZumRails' && (scope.activeProvider === null || p.provider === scope.activeProvider),
  ).length;
  const counts = new Map<string, number>();
  for (const c of cardholders.items) counts.set(c.programName, (counts.get(c.programName) ?? 0) + 1);
  return {
    tiles: [
      ['Cardholders', String(cardholders.totalCount)],
      ['Active Cardholders', String(cardholders.items.filter((c) => c.status === 'Active').length)],
      ['Transactions', String(transactionCount)],
      ['Programs', String(visiblePrograms)],
    ] as const,
    // stable: equal counts keep their first-seen order, as LINQ's OrderByDescending
    programs: [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
  };
}

export function DashboardPage() {
  usePageTitle('Dashboard');
  const { scope } = useStaff();
  const probes = useQuery({
    queryKey: ['dashboard', 'probes'],
    queryFn: async () => {
      const [intercash, opencp] = await Promise.all([adminApi.probe('intercash'), adminApi.probe('opencp')]);
      // ZumRails is not shown (hidden platform-wide); MDP neither (hidden 2026-10-20 — its own page still probes it)
      return [processorStatus('InterCash', intercash), processorStatus('Central Payments', opencp)];
    },
  });
  const figures = useQuery({
    queryKey: ['dashboard', 'figures', scope.activeProvider],
    enabled: !scope.needsProvider,
    queryFn: async () => {
      const programId = effectiveProgramId(scope, null);
      const [cardholders, transactions, programs] = await Promise.all([
        adminApi.cardholders({ provider: scope.activeProvider, programId, page: 1, pageSize: 100 }),
        adminApi.transactions({ provider: scope.activeProvider, programId, page: 1, pageSize: 1 }),
        adminApi.programs(),
      ]);
      return dashboardFigures(scope, cardholders, transactions.totalCount, programs);
    },
  });

  return (
    <>
      <PageHeading
        title="Dashboard"
        actions={
          !scope.needsProvider && (
            <Link className="qor-button qor-button-primary" to="/cardholders/new">
              <Icon name="plus" size={16} />
              Add cardholder
            </Link>
          )
        }
      />

      <div className="qc-dashboard-canvas">
        <section>
          <h2 className="qc-section-title">Processor status</h2>
          <div className="qc-processor-row">
            {(probes.data ?? []).map((status) => (
              <div
                key={status.name}
                className={`qc-processor-card ${status.reachable ? 'qc-processor-card--up' : 'qc-processor-card--down'}`}
                title={status.message}
              >
                <span className="qc-processor-dot" aria-hidden="true"></span>
                <div className="qc-processor-info">
                  <strong>{status.name}</strong>
                  <small>{status.reachable ? 'Operational' : 'Unreachable'}</small>
                </div>
                <span className="qc-processor-latency">{status.elapsedMs}ms</span>
              </div>
            ))}
          </div>
        </section>

        {scope.needsProvider ? (
          <section className="qc-panel">
            <p className="qc-empty">
              Choose a processor above to see its cardholders, transactions, and programs. Processors are never mixed
              together.
            </p>
          </section>
        ) : (
          figures.data && (
            <>
              <section className="qc-stat-row" aria-label="Figures">
                {figures.data.tiles.map(([label, value], i) => (
                  <div key={label} className={`qc-stat-tile ${TILE_CLASSES[i % TILE_CLASSES.length]}`}>
                    <span className="qc-stat-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24">{TILE_ICONS[i % TILE_ICONS.length]}</svg>
                    </span>
                    <span className="qc-stat-label">{label}</span>
                    <strong className="qc-stat-value">{value}</strong>
                  </div>
                ))}
              </section>

              <section className="qc-panel qc-provider-panel">
                <div className="qc-panel-title">
                  <h3>Cardholders by Program</h3>
                  <Link to="/cardholders">All cardholders</Link>
                </div>
                {figures.data.programs.length > 0 ? (
                  <ul className="qor-provider-list qc-provider-list">
                    {figures.data.programs.map((p) => (
                      <li key={p.name}>
                        <article>
                          <span>{p.name}</span>
                        </article>
                        <div className="qc-share-bar" aria-hidden="true">
                          <span style={{ width: `${(p.count / figures.data.programs[0]!.count) * 100}%` }}></span>
                        </div>
                        <span className="qc-badge qc-badge-accent">
                          {p.count} cardholder{p.count === 1 ? '' : 's'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="qc-section-hint">No cardholders yet.</p>
                )}
              </section>
            </>
          )
        )}
      </div>
    </>
  );
}
